import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiEye } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import { db, doc, updateDoc, Timestamp } from '../../config/firebase';
import { suspendEntity, unsuspendEntity, reactivateEntity } from '../../services/adminService';
import EditEntityModal from '../../components/admin/EditEntityModal';
import { formatAddress, safeText } from '../../utils/format';
import styles from './Organizations.module.css';

// Schema (verified against production documents, 2026-08-21):
//   organizations.{ organizationName, shortName, email, telephone, address,
//                   country, openHours, serviceRegionZipCodes[],
//                   additionalInfo, status, verified, approvedAt, createdAt }
// The old code read `name`, `phone`, `type` and `contactName` — none of which
// exist — so the Organization column rendered "(unnamed)" and the detail modal
// was mostly empty. `orgName` below resolves the real field with the legacy
// names kept as a fallback.
const orgName = (o) => o?.organizationName || o?.name || o?.shortName || '';
const orgPhone = (o) => o?.telephone || o?.phone || '';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const Organizations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(500)], []);
  const { data: orgs, loading, error } = useFirestoreCollection('organizations', constraints);

  useEffect(() => {
    if (statusFilter !== 'pending') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter, setSearchParams]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return orgs;
    return orgs.filter((o) => (o.status || 'pending') === statusFilter);
  }, [orgs, statusFilter]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const handleApprove = async (org) => {
    if (!window.confirm(`Approve "${orgName(org)}"?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await updateDoc(doc(db, 'organizations', org.id), {
        status: 'approved',
        approvedAt: Timestamp.now(),
      });
      setShowDetail(false);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Approve failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await updateDoc(doc(db, 'organizations', selected.id), {
        status: 'rejected',
        rejectionReason: rejectReason,
        rejectedAt: Timestamp.now(),
      });
      setShowRejectModal(false);
      setShowDetail(false);
      setRejectReason('');
    } catch (err) {
      console.error(err);
      setErrorMsg(`Reject failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selected) return;
    if (!window.confirm(`Suspend ${orgName(selected)}?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await suspendEntity({ entityType: 'organization', entityId: selected.id, reason: 'Suspended by admin' });
      setShowDetail(false);
      setSelected(null);
    } catch (err) {
      setErrorMsg(`Suspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!selected) return;
    if (!window.confirm(`Reinstate ${orgName(selected)}?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await unsuspendEntity({ entityType: 'organization', entityId: selected.id });
      setShowDetail(false);
      setSelected(null);
    } catch (err) {
      setErrorMsg(`Unsuspend failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selected) return;
    if (!window.confirm(`Reactivate this rejected organization back to pending? They can resubmit / be reapproved.`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await reactivateEntity({ entityType: 'organization', entityId: selected.id });
      setShowDetail(false);
      setSelected(null);
    } catch (err) {
      setErrorMsg(`Reactivate failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const variants = { pending: 'warning', approved: 'success', rejected: 'danger' };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'pending'}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name', accessor: (o) => orgName(o) },
    { key: 'shortName', label: 'Short name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone', accessor: (o) => orgPhone(o) },
    { key: 'country', label: 'Country' },
    { key: 'serviceRegionZipCodes', label: 'Service zips', accessor: (o) => (Array.isArray(o.serviceRegionZipCodes) ? o.serviceRegionZipCodes.join(' ') : '') },
    { key: 'verified', label: 'Verified', accessor: (o) => (o.verified ? 'yes' : 'no') },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Submitted', accessor: (o) => formatDate(o.createdAt) },
  ];

  const columns = [
    {
      key: 'name',
      title: 'Organization',
      render: (_, row) => (
        <div>
          <div className={styles.name}>{orgName(row) || '(unnamed)'}</div>
          <div className={styles.meta}>
            {[row.shortName, row.country].filter(Boolean).join(' · ') || row.email || '—'}
          </div>
        </div>
      ),
    },
    { key: 'email', title: 'Email', render: (v) => v || '-' },
    { key: 'status', title: 'Status', render: (v) => statusBadge(v || 'pending') },
    { key: 'createdAt', title: 'Submitted', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '210px',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            onClick={() => {
              setSelected(row);
              setShowDetail(true);
            }}
          />
          {(row.status || 'pending') === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                icon={<FiCheck />}
                onClick={() => handleApprove(row)}
                disabled={actionLoading}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<FiX />}
                onClick={() => {
                  setSelected(row);
                  setShowRejectModal(true);
                }}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Organizations</h1>
          {!loading && <Badge variant="primary">{orgs.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Approve or reject organization registrations
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error: {error.message}</div>
        </Card>
      )}
      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-organizations-${statusFilter}`} />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No organizations" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        title={selected?.name || 'Organization'}
        size="lg"
        footer={
          (() => {
            const status = selected?.status || 'pending';
            const isSuspended = !!selected?.isSuspended;
            if (status === 'pending') {
              return (
                <>
                  <Button variant="ghost" onClick={() => setShowDetail(false)}>Close</Button>
                  <Button variant="danger" onClick={() => { setShowDetail(false); setShowRejectModal(true); }}>Reject</Button>
                  <Button variant="primary" loading={actionLoading} onClick={() => handleApprove(selected)}>Approve</Button>
                </>
              );
            }
            if (isSuspended) {
              return (
                <>
                  <Button variant="ghost" onClick={() => setShowDetail(false)}>Close</Button>
                  <Button variant="ghost" onClick={() => setShowEditModal(true)}>Edit profile</Button>
                  <Button variant="primary" loading={actionLoading} onClick={handleUnsuspend}>Unsuspend</Button>
                </>
              );
            }
            if (status === 'approved') {
              return (
                <>
                  <Button variant="ghost" onClick={() => setShowDetail(false)}>Close</Button>
                  <Button variant="ghost" onClick={() => setShowEditModal(true)}>Edit profile</Button>
                  <Button variant="danger" loading={actionLoading} onClick={handleSuspend}>Suspend</Button>
                </>
              );
            }
            if (status === 'rejected') {
              return (
                <>
                  <Button variant="ghost" onClick={() => setShowDetail(false)}>Close</Button>
                  <Button variant="primary" loading={actionLoading} onClick={handleReactivate}>Reactivate (back to pending)</Button>
                </>
              );
            }
            return <Button variant="ghost" onClick={() => setShowDetail(false)}>Close</Button>;
          })()
        }
      >
        {selected && (
          <div className={styles.detail}>
            <div className={styles.grid}>
              <div className={styles.item}>
                <span className={styles.label}>Name</span>
                <span className={styles.value}>{safeText(orgName(selected))}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Short name</span>
                <span className={styles.value}>{safeText(selected.shortName)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Email</span>
                <span className={styles.value}>{safeText(selected.email)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Phone</span>
                <span className={styles.value}>{safeText(orgPhone(selected))}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Country</span>
                <span className={styles.value}>{safeText(selected.country)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Verified</span>
                <span className={styles.value}>{selected.verified ? 'Yes' : 'No'}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Open hours</span>
                <span className={styles.value}>{safeText(selected.openHours)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Service region</span>
                <span className={styles.value}>
                  {Array.isArray(selected.serviceRegionZipCodes) && selected.serviceRegionZipCodes.length
                    ? selected.serviceRegionZipCodes.join(', ')
                    : '-'}
                </span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Approved</span>
                <span className={styles.value}>{formatDate(selected.approvedAt)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Status</span>
                <span className={styles.value}>{statusBadge(selected.status || 'pending')}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Submitted</span>
                <span className={styles.value}>{formatDate(selected.createdAt)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Address</span>
                <span className={styles.value}>{formatAddress(selected.address)}</span>
              </div>
              <div className={styles.item}>
                <span className={styles.label}>Additional info</span>
                <span className={styles.value}>{safeText(selected.additionalInfo)}</span>
              </div>
            </div>
            {selected.description && (
              <div className={styles.section}>
                <h4>Description</h4>
                <p>{selected.description}</p>
              </div>
            )}
            {selected.rejectionReason && (
              <div className={styles.section}>
                <h4>Rejection Reason</h4>
                <p>{selected.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        title="Reject Organization"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleReject} loading={actionLoading} disabled={!rejectReason}>
              Reject
            </Button>
          </>
        }
      >
        <p>Reason for rejecting <strong>{selected?.name}</strong>:</p>
        <Input
          placeholder="Rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      <EditEntityModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        entityType="organization"
        entity={selected}
        onSaved={() => {
          // Re-pull fresh org doc so the detail modal reflects the edit. Since
          // there's no getOrgById helper here we read directly via the live
          // useFirestoreCollection cache by re-selecting on next click; for now
          // just close detail too.
          setShowDetail(false);
        }}
      />
    </div>
  );
};

export default Organizations;
